import moment from 'moment';

import {
  Carriage,
  SendCommunicationCustomersSelection,
  Typology,
  ConflictType,
  SendCommunicationPurchaseComparisonValues,
} from 'types';
import { Constants } from 'core';
import { Translate } from 'core/i18n/types';
import { getSelectPurchaseDateTimeList } from 'common/utils/send-communication';

export const calculateClientsType = (
  customersSelection: SendCommunicationCustomersSelection,
  translate: ReturnType<Translate>
): string => {
  if (customersSelection === SendCommunicationCustomersSelection.All) {
    return translate('sendCommunication', 'reminderSelections', 'clientsTypeValue', 'all');
  }
  if (customersSelection === SendCommunicationCustomersSelection.Ascending) {
    return translate('sendCommunication', 'reminderSelections', 'clientsTypeValue', 'ascend');
  }
  if (customersSelection === SendCommunicationCustomersSelection.Descending) {
    return translate('sendCommunication', 'reminderSelections', 'clientsTypeValue', 'descend');
  }
  if (customersSelection === SendCommunicationCustomersSelection.AscendingDescending) {
    return translate('sendCommunication', 'reminderSelections', 'clientsTypeValue', 'ascendDescend');
  }
  if (customersSelection === SendCommunicationCustomersSelection.DVNumbers) {
    return translate('sendCommunication', 'reminderSelections', 'DVNumbersValue');
  }

  return '';
};

export const calculateImpactedStations = (
  impactedStations: string[],
  stationsConcerned: Array<{ shortCode: string; uicStation: string }>
): string => {
  const shortCodesList: string[] = [];
  stationsConcerned?.forEach((station) => {
    if (impactedStations.includes(station.uicStation)) {
      shortCodesList.push(station.shortCode);
    }
  });
  return shortCodesList.join(', ');
};

export const calculateExcludedStations = (
  excludedStations: string[],
  stationsConcerned: Array<{ shortCode: string; uicStation: string }>
): string => {
  const shortCodesList: string[] = [];
  stationsConcerned?.forEach((station) => {
    if (excludedStations.includes(station.uicStation)) {
      shortCodesList.push(station.shortCode);
    }
  });
  return shortCodesList.join(', ');
};

export const calculateSelectedCarriages = (
  selectedCarriages: Array<Omit<Carriage, 'wholeCarriageSelected'>>,
  selectedServiceType: string | undefined,
  translate: ReturnType<Translate>
): string => {
  const {
    OUIGO: { FIRST_TRAIN: OuigoFirstTrain, SECOND_TRAIN: OuigoSecondTrain },
    OUIGO_ESP: { FIRST_TRAIN: OuigoEspFirstTrain, SECOND_TRAIN: OuigoEspSecondTrain },
    OUIGO_LC: OuigoLcTrain,
    OUIGO_LB: OuigoLbTrain,
  } = Constants.TRAIN_CONFIGURATION;

  let totalCarriages: number;
  switch (selectedServiceType) {
    case 'OUIGO':
      totalCarriages = OuigoFirstTrain.length + OuigoSecondTrain.length;
      break;
    case 'OUIGO_ESP':
      totalCarriages = OuigoEspFirstTrain.length + OuigoEspSecondTrain.length;
      break;
    case 'OUIGO_LC':
      totalCarriages = OuigoLcTrain.length;
      break;
    case 'OUIGO_LB':
      totalCarriages = OuigoLbTrain.length;
      break;
    default:
      totalCarriages = OuigoFirstTrain.length + OuigoSecondTrain.length;
      break;
  }
  const allIsNotSelected = selectedCarriages.filter((item) => item.upStage === false || item.downStage === false);

  if (selectedCarriages?.length === totalCarriages && allIsNotSelected?.length === 0) {
    return translate('sendCommunication', 'reminderSelections', 'selectedCarriagesTypeValue', 'all');
  }

  const selectedCarriagesParsed = selectedCarriages.map((item) => {
    if ((item.downStage && item.upStage) || ['OUIGO_LC', 'OUIGO_LB'].includes(selectedServiceType || '')) {
      return item.carriageNumber;
    }

    if (item.upStage) {
      return translate('sendCommunication', 'reminderSelections', 'selectedCarriagesTypeValue', 'high', {
        num: item.carriageNumber,
      });
    }

    return translate('sendCommunication', 'reminderSelections', 'selectedCarriagesTypeValue', 'down', {
      num: item.carriageNumber,
    });
  });

  return selectedCarriagesParsed.join(', ');
};

export const calculateOthers = ({
  selectedTypologiesIds,
  typologies,
  translate,
  conflictTypes,
  selectedConflictTypeId,
  excludeAllConflictTypes,
  selectedPurchaseComparison,
  selectedPurchaseDate,
  selectedPurchaseTime,
}: {
  selectedTypologiesIds: number[];
  typologies: Typology[];
  translate: ReturnType<Translate>;
  conflictTypes: ConflictType[];
  selectedConflictTypeId: ConflictType['id'] | undefined;
  excludeAllConflictTypes: boolean;
  selectedPurchaseComparison: SendCommunicationPurchaseComparisonValues | undefined;
  selectedPurchaseDate: string | undefined;
  selectedPurchaseTime: string | undefined;
}): string => {
  const selectedTypologies = typologies.filter((item) => item.id && selectedTypologiesIds.includes(item.id));
  const selectedConflictType = conflictTypes.find((conflictType) => selectedConflictTypeId === conflictType.id);

  let otherReminders = '';
  if (selectedTypologies.length) {
    const selectedTypologiesReminder = selectedTypologies.map((item) => item.name).join(', ');
    otherReminders = selectedTypologiesReminder;
  }
  if (selectedConflictTypeId) {
    const selectedConflictedReminder = `${translate('sendCommunication', 'reminderSelections', 'conflictedTickets')} (${
      selectedConflictType?.name
    })`;
    otherReminders = otherReminders ? `${otherReminders} / ${selectedConflictedReminder}` : selectedConflictedReminder;
  }
  if (excludeAllConflictTypes) {
    const excludeAllConflictedReminder = translate(
      'sendCommunication',
      'reminderSelections',
      'excludeAllConflictedTickets'
    );
    otherReminders = otherReminders
      ? `${otherReminders} / ${excludeAllConflictedReminder}`
      : excludeAllConflictedReminder;
  }

  if (!!selectedPurchaseComparison && !!selectedPurchaseDate && !!selectedPurchaseTime) {
    const selectedPurchaseComparisonName = getSelectPurchaseDateTimeList(translate).find(
      (item) => item.id === selectedPurchaseComparison
    )?.name;
    const selectedPurchaseDateFormatted = moment(selectedPurchaseDate).format('DD/MM/YYYY');
    const selectedPurchaseTimeFormatted = selectedPurchaseTime.replace(':', 'h');
    const selectPurchaseDateTime = translate('sendCommunication', 'reminderSelections', 'selectPurchaseDateTime', {
      selectedPurchaseComparison: selectedPurchaseComparisonName,
      selectedPurchaseDate: selectedPurchaseDateFormatted,
      selectedPurchaseTime: selectedPurchaseTimeFormatted,
    });
    otherReminders = otherReminders ? `${otherReminders} / ${selectPurchaseDateTime}` : selectPurchaseDateTime;
  }

  return otherReminders;
};
